export type TerminologyEditField = 'wording' | 'note';
export type TerminologyEditMode = 'composed' | 'literal';

export interface TerminologyEditSnapshot {
  content: string;
  reason: string;
  mode: TerminologyEditMode;
  field: TerminologyEditField;
  selectionStart: number | null;
  selectionEnd: number | null;
  scrollTop: number;
  scrollLeft: number;
}

export interface TerminologyEditHistoryOptions {
  /** Maximum retained states, including the current state. Oldest states expire first. */
  capacity?: number;
  coalesceMs?: number;
}

export interface TerminologyEditRecordOptions {
  kind: 'typing' | 'atomic';
  timestamp: number;
  /** Refines the current position only when its content, note and mode still match. */
  before?: TerminologyEditSnapshot;
  /** Omit to create a separate undo boundary even for typing. */
  coalesceKey?: string;
}

interface TypingGroup {
  key: string;
  field: TerminologyEditField;
  timestamp: number;
}

const sameValues = (
  left: TerminologyEditSnapshot,
  right: TerminologyEditSnapshot,
): boolean =>
  left.content === right.content &&
  left.reason === right.reason &&
  left.mode === right.mode;

/** Exact, in-memory proposal history. It contains no publication or authorization identities. */
export class TerminologyEditHistory {
  private readonly capacity: number;
  private readonly coalesceMs: number;
  private entries: TerminologyEditSnapshot[] = [];
  private position = -1;
  private group: TypingGroup | null = null;

  constructor(options: TerminologyEditHistoryOptions = {}) {
    this.capacity = options.capacity ?? 80;
    this.coalesceMs = options.coalesceMs ?? 750;
    if (
      !Number.isSafeInteger(this.capacity) ||
      this.capacity < 1 ||
      !Number.isFinite(this.coalesceMs) ||
      this.coalesceMs < 0
    )
      throw new RangeError(
        'Terminology history limits must be finite and bounded.',
      );
  }

  get current(): TerminologyEditSnapshot | null {
    const current = this.entries[this.position];
    return current ? { ...current } : null;
  }

  get canUndo(): boolean {
    return this.position > 0;
  }

  get canRedo(): boolean {
    return this.position >= 0 && this.position < this.entries.length - 1;
  }

  reset(snapshot: TerminologyEditSnapshot): void {
    this.clear();
    this.entries = [{ ...snapshot }];
    this.position = 0;
  }

  clear(): void {
    this.entries = [];
    this.position = -1;
    this.breakGroup();
  }

  breakGroup(): void {
    this.group = null;
  }

  /** Selection-only changes refine the current state and retain any future redo branch. */
  record(
    next: TerminologyEditSnapshot,
    options: TerminologyEditRecordOptions,
  ): boolean {
    if (!Number.isFinite(options.timestamp))
      throw new RangeError('Terminology history timestamps must be finite.');

    const current = this.entries[this.position];
    if (!current) {
      this.reset(next);
      return false;
    }
    if (sameValues(current, next)) {
      this.entries[this.position] = { ...next };
      this.breakGroup();
      return false;
    }

    if (options.before && sameValues(current, options.before))
      this.entries[this.position] = { ...options.before };

    const group = this.group;
    const coalesce =
      options.kind === 'typing' &&
      options.coalesceKey !== undefined &&
      group !== null &&
      group.key === options.coalesceKey &&
      group.field === next.field &&
      options.timestamp >= group.timestamp &&
      this.coalesceMs > 0 &&
      options.timestamp - group.timestamp <= this.coalesceMs &&
      this.position > 0 &&
      this.position === this.entries.length - 1;

    this.entries.splice(this.position + 1);
    if (coalesce) this.entries[this.position] = { ...next };
    else {
      this.entries.push({ ...next });
      this.position += 1;
      if (this.entries.length > this.capacity) {
        this.entries.shift();
        this.position -= 1;
      }
    }
    this.group =
      options.kind === 'typing' && options.coalesceKey !== undefined
        ? {
            key: options.coalesceKey,
            field: next.field,
            timestamp: options.timestamp,
          }
        : null;
    return true;
  }

  undo(): TerminologyEditSnapshot | null {
    this.breakGroup();
    if (!this.canUndo) return null;
    this.position -= 1;
    return this.current;
  }

  redo(): TerminologyEditSnapshot | null {
    this.breakGroup();
    if (!this.canRedo) return null;
    this.position += 1;
    return this.current;
  }
}
